import { useState } from "react";
import { motion } from "framer-motion";
import foxImg from "../../../assets/reels_content/fox-front-pose.png";
import { sendContactQuery } from "../../../controllers/contactController";
const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [apiError, setApiError] = useState("");

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = "Name is required";
    if (!formData.email.trim()) newErrors.email = "Email is required";
    else if (!/\S+@\S+\.\S+/.test(formData.email))
      newErrors.email = "Email is invalid";
    if (!formData.message.trim()) newErrors.message = "Message is required";
    return newErrors;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const resetForm = () => {
    setFormData({ name: "", email: "", message: "" });
    setErrors({});
    setApiError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const newErrors = validateForm();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setApiError("");
      return;
    }

    setErrors({});
    setApiError("");
    setIsSubmitting(true);

    try {
      await sendContactQuery(formData);
      setIsSuccess(true);
      resetForm();
      // Reset success message after 5 seconds
      setTimeout(() => {
        setIsSuccess(false);
      }, 5000);
    } catch (err) {
      setApiError(err.message || "Failed to send message. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };
  return (
    <section className="bg-bggray  dark:bg-gray-800 text-black dark:text-textDark py-16 sm:py-24 transition-colors duration-300 relative overflow-hidden">
      <div className="w-full max-w-[860px] mx-auto px-6 relative z-10">
        {/* Heading */}
        <motion.div
          className="text-center max-w-[560px] mx-auto "
          initial={{ opacity: 0, y: -12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.25 }}
          transition={{ duration: 0.6 }}
        >
          <p className="text-text/70 dark:text-textDark/70 text-xs font-semibold mb-2">
            Contact Us
          </p>
          <h2 className="text-[28px] sm:text-[32px] font-extrabold tracking-tight mb-3">
            Get in touch
          </h2>
          <p className="text-text/80 dark:text-textDark/80 text-[14px] leading-relaxed">
            Reach out with your questions or feedback. Our team is ready to
            assist you quickly and efficiently.
          </p>
        </motion.div>

        {/* Form */}
        <motion.form
          onSubmit={handleSubmit}
          className="mt-8 sm:mt-10 max-w-[560px] dark:bg-darkbg mx-auto bg-surface dark:bg-surfaceDark border border-border dark:border-none rounded-xl p-6 shadow-sm"
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.25 }}
          transition={{ duration: 0.6 }}
        >
          <label className="block text-[12px] font-medium mb-1 text-text/80 dark:text-textDark/80">
            Name
          </label>
          <input
            name="name"
            value={formData.name}
            onChange={handleChange}
            type="text"
            placeholder="Name"
            className={`w-full h-10 rounded-md bg-background dark:bg-darkbg border ${
              errors.name
                ? "border-red-500"
                : "border-border dark:border-borderDark"
            } px-3 text-sm text-text dark:text-textDark placeholder:text-text/60 dark:placeholder:text-textDark/60 outline-none focus:ring-2 focus:ring-primary/30 transition-colors`}
          />
          {errors.name && (
            <p className="mt-1 text-xs text-red-600 dark:text-red-400">
              {errors.name}
            </p>
          )}

          <label className="block text-[12px] font-medium mt-3 mb-1 text-text/80 dark:text-textDark/80">
            Email
          </label>
          <input
            name="email"
            value={formData.email}
            onChange={handleChange}
            type="email"
            placeholder="Email"
            className={`w-full h-10 rounded-md bg-background dark:bg-darkbg border ${
              errors.email
                ? "border-red-500"
                : "border-border dark:border-borderDark"
            } px-3 text-sm text-text dark:text-textDark placeholder:text-text/60 dark:placeholder:text-textDark/60 outline-none focus:ring-2 focus:ring-primary/30 transition-colors`}
          />
          {errors.email && (
            <p className="mt-1 text-xs text-red-600 dark:text-red-400">
              {errors.email}
            </p>
          )}

          <label className="block text-[12px] font-medium mt-3 mb-1 text-text/80 dark:text-textDark/80">
            Message
          </label>
          <textarea
            name="message"
            value={formData.message}
            onChange={handleChange}
            rows={5}
            placeholder="Type your message"
            className={`w-full rounded-md bg-background dark:bg-darkbg border ${
              errors.message
                ? "border-red-500"
                : "border-border dark:border-borderDark"
            } px-3 py-2 text-sm text-text dark:text-textDark placeholder:text-text/60 dark:placeholder:text-textDark/60 resize-none outline-none focus:ring-2 focus:ring-primary/30 transition-colors`}
          />
          {errors.message && (
            <p className="mt-1 text-xs text-red-600 dark:text-red-400">
              {errors.message}
            </p>
          )}

          <div className="mt-3 flex items-start gap-2 text-[13px]">
            <input
              id="agree"
              type="checkbox"
              required
              className="mt-[2px] h-4 w-4 rounded border-border dark:border-borderDark accent-primary transition-colors"
            />
            <label
              htmlFor="agree"
              className="text-text/70 dark:text-textDark/70"
            >
              By selecting this you agree to our{" "}
              <a
                href="/privacy"
                className="text-primary underline underline-offset-2"
              >
                Privacy Policy
              </a>
              .
            </label>
          </div>

          {/* Alert area: show either API error (red) or success (green) in the same place */}
          {(apiError || isSuccess) && (
            <div
              className={`mt-3 p-3 rounded-md text-sm ${
                apiError
                  ? "bg-red-100 dark:bg-red-900 border border-red-400 dark:border-red-700 text-red-700 dark:text-red-300"
                  : "bg-green-100 dark:bg-green-900 border border-green-400 dark:border-green-700 text-green-700 dark:text-green-300 flex items-center gap-2"
              }`}
            >
              {apiError ? (
                apiError
              ) : (
                <>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    fill="currentColor"
                    className="bi bi-check-circle-fill"
                    viewBox="0 0 16 16"
                  >
                    <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zm-3.97-3.03a.75.75 0 0 0-1.08.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-.01-1.05z" />
                  </svg>
                  Message sent successfully! We'll get back to you soon.
                </>
              )}
            </div>
          )}

          <button
            type="submit"
            disabled={isSubmitting}
            className={`mt-3 w-full h-10 rounded-md bg-primary text-white text-sm font-semibold transition shadow-sm flex items-center justify-center ${
              isSubmitting
                ? "opacity-75 cursor-not-allowed"
                : "hover:bg-primary-hover"
            }`}
          >
            {isSubmitting ? (
              <>
                <svg
                  className="animate-spin -ml-1 mr-2 h-5 w-5 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                Sending...
              </>
            ) : (
              "Send message"
            )}
          </button>
        </motion.form>
      </div>

      {/* Mascot */}
      <motion.img
        src={foxImg}
        alt="Fox mascot"
        className="hidden md:block absolute right-[20px] lg:right-[320px] bottom-28 w-28 sm:w-32 select-none pointer-events-none"
        initial={{ opacity: 0, y: 12 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.2 }}
        transition={{ duration: 0.5 }}
      />
    </section>
  );
};

export default ContactSection;
